
import React, { useState } from 'react';
import { VideoLength, StoryLanguage, StoryStructure } from '../types';
import type { StoryInputFormProps } from '../types';

const lengthOptions = [
  { value: VideoLength.FULL_8S, label: '8초 스토리', segments: 1 },
  { value: VideoLength.FULL_16S, label: '16초 스토리', segments: 2 },
  { value: VideoLength.FULL_24S, label: '24초 스토리', segments: 3 },
];

const languageOptions = [
  { value: StoryLanguage.KOREAN, label: '한국어', flag: '🇰🇷' },
  { value: StoryLanguage.ENGLISH, label: 'English', flag: '🇺🇸' },
  { value: StoryLanguage.JAPANESE, label: '日本語', flag: '🇯🇵' },
  { value: StoryLanguage.CHINESE, label: '中文', flag: '🇨🇳' },
  { value: StoryLanguage.SPANISH, label: 'Español', flag: '🇪🇸' },
  { value: StoryLanguage.FRENCH, label: 'Français', flag: '🇫🇷' },
  { value: StoryLanguage.GERMAN, label: 'Deutsch', flag: '🇩🇪' },
  { value: StoryLanguage.RUSSIAN, label: 'Русский', flag: '🇷🇺' },
];

const structureOptions = [
  { 
    value: StoryStructure.HOOK_TO_END, 
    label: '끝까지 보게 되는 스토리', 
    icon: '🎯',
    description: '강력한 훅으로 시작해 끝까지 시선을 붙잡는 구성'
  },
  { 
    value: StoryStructure.DRAMATIC_TWIST, 
    label: '극적인 반전', 
    icon: '🔄',
    description: '예상치 못한 반전으로 충격을 주는 스토리'
  },
  { 
    value: StoryStructure.EMOTIONAL_JOURNEY, 
    label: '감동적인 여정', 
    icon: '❤️',
    description: '마음을 울리는 감동적인 스토리'
  },
  { 
    value: StoryStructure.COMEDY_BUILDUP, 
    label: '코미디 빌드업', 
    icon: '😂',
    description: '웃음이 점점 커지는 유머러스한 구성'
  },
  { 
    value: StoryStructure.MYSTERY_REVEAL, 
    label: '미스터리 해결', 
    icon: '🔍',
    description: '궁금증을 유발하고 해답을 제시하는 구조'
  },
  { 
    value: StoryStructure.TUTORIAL_STEPS, 
    label: '단계별 튜토리얼', 
    icon: '📚',
    description: '따라하기 쉬운 단계별 설명'
  },
  { 
    value: StoryStructure.BEFORE_AFTER, 
    label: '비포/애프터', 
    icon: '✨',
    description: '극적인 변화를 보여주는 구성'
  },
  { 
    value: StoryStructure.CHALLENGE_RESULT, 
    label: '도전과 결과', 
    icon: '🏆',
    description: '도전 과정과 놀라운 결과를 보여주는 구조'
  },
  { 
    value: StoryStructure.FACTS_SURPRISE, 
    label: '놀라운 사실들', 
    icon: '💡',
    description: '충격적이고 흥미로운 사실들을 공개'
  },
  { 
    value: StoryStructure.STORY_LOOP, 
    label: '순환 구조', 
    icon: '♾️',
    description: '처음과 끝이 연결되는 순환 스토리'
  },
];

const StoryInputForm: React.FC<StoryInputFormProps> = ({ onGenerate, isLoading }) => {
  const [topic, setTopic] = useState('');
  const [length, setLength] = useState<VideoLength>(VideoLength.FULL_16S);
  const [customLength, setCustomLength] = useState<number>(30);
  const [useCustomLength, setUseCustomLength] = useState<boolean>(false);
  const [language, setLanguage] = useState<StoryLanguage>(StoryLanguage.KOREAN);
  const [structure, setStructure] = useState<StoryStructure>(StoryStructure.HOOK_TO_END);

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    e.stopPropagation();
    
    // Prevent form from causing any layout shifts
    const button = e.currentTarget.querySelector('button[type="submit"]') as HTMLButtonElement;
    if (button) {
      button.blur();
    }
    
    if (topic.trim()) {
      // Use setTimeout to ensure UI doesn't jump
      setTimeout(() => {
        const finalLength = useCustomLength ? customLength : length;
        onGenerate(topic, finalLength as any, language, structure);
      }, 0);
    }
  };

  return (
    <div className="bg-slate-800/60 p-6 rounded-2xl shadow-2xl border border-slate-700">
      <form onSubmit={handleSubmit} className="space-y-6">
        <div>
          <label htmlFor="topic" className="block text-lg font-medium text-slate-300 mb-2">
            Video Topic
          </label>
          <textarea
            id="topic"
            name="topic"
            rows={3}
            className="w-full bg-slate-900/70 border border-slate-600 rounded-lg shadow-sm focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 text-slate-200 p-3 transition duration-200 placeholder:text-slate-500"
            placeholder="e.g., A funny cat video, tips for growing houseplants, a 1-minute travel guide to Tokyo..."
            value={topic}
            onChange={(e) => setTopic(e.target.value)}
            required
            disabled={isLoading}
          />
        </div>

        <div>
          <label className="block text-lg font-medium text-slate-300 mb-3">
            스토리 구성 방식
          </label>
          <div className="grid grid-cols-2 gap-3">
            {structureOptions.map((option) => (
              <label key={option.value} className="cursor-pointer">
                <input
                  type="radio"
                  name="structure"
                  value={option.value}
                  checked={structure === option.value}
                  onChange={() => setStructure(option.value)}
                  className="sr-only"
                  disabled={isLoading}
                />
                <div
                  className={`p-3 rounded-lg border-2 transition duration-200 ${
                    structure === option.value
                      ? 'bg-indigo-600/20 border-indigo-500 shadow-lg'
                      : 'bg-slate-700/50 border-slate-600 hover:bg-slate-600/50 hover:border-slate-500'
                  }`}
                >
                  <div className="flex items-center mb-1">
                    <span className="text-2xl mr-2">{option.icon}</span>
                    <span className={`font-semibold ${
                      structure === option.value ? 'text-indigo-300' : 'text-slate-200'
                    }`}>
                      {option.label}
                    </span>
                  </div>
                  <p className={`text-xs ${
                    structure === option.value ? 'text-indigo-200' : 'text-slate-400'
                  }`}>
                    {option.description}
                  </p>
                </div>
              </label>
            ))}
          </div>
        </div>

        <div>
          <label className="block text-lg font-medium text-slate-300 mb-3">
            📹 비디오 길이 선택
          </label>
          <div className="space-y-3">
            {/* Preset length options */}
            <div className="grid grid-cols-4 gap-3">
              {lengthOptions.map((option) => (
                <label key={option.value} className="cursor-pointer">
                  <input
                    type="radio"
                    name="length"
                    value={option.value}
                    checked={!useCustomLength && length === option.value}
                    onChange={() => {
                      setLength(option.value);
                      setUseCustomLength(false);
                    }}
                    className="sr-only"
                    disabled={isLoading}
                  />
                  <div
                    className={`text-center p-3 rounded-lg border-2 transition duration-200 ${
                      !useCustomLength && length === option.value
                        ? 'bg-indigo-600 border-indigo-500 text-white font-bold shadow-lg'
                        : 'bg-slate-700 border-slate-600 hover:bg-slate-600/50 hover:border-slate-500'
                    }`}
                  >
                    <div className="font-semibold">{option.label}</div>
                    <div className="text-xs mt-1 opacity-80">
                      {option.segments}개 세그먼트
                    </div>
                  </div>
                </label>
              ))}
              
              {/* Custom length option as 4th option */}
              <label className="cursor-pointer">
                <input
                  type="radio"
                  name="length"
                  checked={useCustomLength}
                  onChange={() => setUseCustomLength(true)}
                  className="sr-only"
                  disabled={isLoading}
                />
                <div
                  className={`text-center p-3 rounded-lg border-2 transition duration-200 h-full flex flex-col justify-center ${
                    useCustomLength
                      ? 'bg-indigo-600 border-indigo-500 text-white font-bold shadow-lg'
                      : 'bg-slate-700 border-slate-600 hover:bg-slate-600/50 hover:border-slate-500'
                  }`}
                >
                  <div className="font-semibold">⚙️ 사용자 정의</div>
                  <div className="text-xs mt-1 opacity-80">
                    5~120초
                  </div>
                </div>
              </label>
            </div>
            
            {/* Custom length input - shown when selected */}
            {useCustomLength && (
              <div className="p-4 rounded-lg bg-indigo-600/10 border-2 border-indigo-500/50">
                <div className="flex items-center justify-center gap-4">
                  <span className="text-slate-300 font-medium">비디오 길이:</span>
                  <div className="flex items-center gap-2">
                    <button
                      type="button"
                      onClick={() => setCustomLength(Math.max(5, customLength - 5))}
                      disabled={isLoading || customLength <= 5}
                      className="px-3 py-1 bg-slate-700 hover:bg-slate-600 disabled:bg-slate-800 disabled:opacity-50 text-white rounded transition"
                    >
                      −5
                    </button>
                    <input
                      type="number"
                      min="5"
                      max="120"
                      value={customLength}
                      onChange={(e) => setCustomLength(Math.min(120, Math.max(5, parseInt(e.target.value) || 5)))}
                      disabled={isLoading}
                      className="w-24 px-3 py-2 bg-slate-800 border border-slate-600 rounded text-white text-center font-bold text-lg"
                    />
                    <span className="text-slate-300 font-medium">초</span>
                    <button
                      type="button"
                      onClick={() => setCustomLength(Math.min(120, customLength + 5))}
                      disabled={isLoading || customLength >= 120}
                      className="px-3 py-1 bg-slate-700 hover:bg-slate-600 disabled:bg-slate-800 disabled:opacity-50 text-white rounded transition"
                    >
                      +5
                    </button>
                  </div>
                </div>
                <div className="text-center mt-3">
                  <span className="text-sm text-slate-400">
                    💡 API를 통해 {customLength}초 길이의 비디오를 한 번에 생성합니다
                  </span>
                </div>
                <div className="flex justify-center gap-3 mt-3">
                  <button
                    type="button"
                    onClick={() => setCustomLength(15)}
                    className="px-3 py-1 bg-slate-700/50 hover:bg-slate-600/50 text-slate-300 text-xs rounded transition"
                  >
                    15초
                  </button>
                  <button
                    type="button"
                    onClick={() => setCustomLength(30)}
                    className="px-3 py-1 bg-slate-700/50 hover:bg-slate-600/50 text-slate-300 text-xs rounded transition"
                  >
                    30초
                  </button>
                  <button
                    type="button"
                    onClick={() => setCustomLength(60)}
                    className="px-3 py-1 bg-slate-700/50 hover:bg-slate-600/50 text-slate-300 text-xs rounded transition"
                  >
                    60초
                  </button>
                  <button
                    type="button"
                    onClick={() => setCustomLength(90)}
                    className="px-3 py-1 bg-slate-700/50 hover:bg-slate-600/50 text-slate-300 text-xs rounded transition"
                  >
                    90초
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>

        <div>
          <label className="block text-lg font-medium text-slate-300 mb-3">
            스토리 언어
          </label>
          <div className="grid grid-cols-4 gap-2">
            {languageOptions.map((option) => (
              <label key={option.value} className="flex">
                <input
                  type="radio"
                  name="language"
                  value={option.value}
                  checked={language === option.value}
                  onChange={() => setLanguage(option.value)}
                  className="sr-only"
                  disabled={isLoading}
                />
                <div
                  className={`cursor-pointer text-center p-2 rounded-lg border-2 transition duration-200 flex-1 ${
                    language === option.value
                      ? 'bg-indigo-600 border-indigo-500 text-white font-bold shadow-lg'
                      : 'bg-slate-700 border-slate-600 hover:bg-slate-600/50 hover:border-slate-500'
                  }`}
                >
                  <div className="text-xl mb-1">{option.flag}</div>
                  <div className="text-xs">{option.label}</div>
                </div>
              </label>
            ))}
          </div>
        </div>

        <div>
          <button
            type="button"
            disabled={isLoading || !topic.trim()}
            onClick={(e) => {
              e.preventDefault();
              e.stopPropagation();
              handleSubmit(e as any);
            }}
            className="w-full flex justify-center items-center py-3 px-4 border border-transparent rounded-lg shadow-lg text-base font-medium text-white bg-indigo-600 hover:bg-indigo-700 disabled:bg-slate-600 disabled:cursor-not-allowed transition duration-300 ease-in-out transform hover:scale-105 disabled:scale-100"
          >
            {isLoading ? (
              <>
                <svg className="animate-spin -ml-1 mr-3 h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                </svg>
                Generating Story...
              </>
            ) : (
              '✨ Generate AI Story'
            )}
          </button>
        </div>
      </form>
    </div>
  );
};

export default StoryInputForm;
